import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { ApiError } from '../../../auth/api-client';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, type GroupCheck, type GroupPostPage as PostData, type GroupReaction, type GroupReply } from '../../api';
import { BottomSheet } from '../../components/BottomSheet';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { plural } from '../../format';
import { goBack, navigate } from '../../router';
import { useKeptAcrossReload } from '../../use-kept-across-reload';
import { CheckView, GROUP_LIMITS, GroupBar, GroupFaceView, GroupField, GroupReactions, HeldNote, MORE, ago, refusalOf, useDictation, useGroupsLive, type Refusal } from './group-parts';
import { withMine } from './group-reactions';

/** Where words are being written: an answer to the post (null), or a reply under one top-level answer (its id). */
type Target = string | null;

/**
 * A post — `/groups/p/:postId` (2026-11-03 founder; mock Post). Who asked and when, the question and its background (or
 * what was shared), how many people have seen it, its emoji, every top-level answer (Ember's under its own name and
 * flame) with its replies indented under it (one level: a reply to a reply goes under the same answer), emoji on each, a
 * report on anything that should not be there (hidden for them after), and their own answer or reply, which Ember looks
 * at before it goes up as it does a post. `?answer=1` (Ember asked them to answer it, or the feed's Answer) opens the
 * answer box.
 */
export function GroupPostPage({ postId, answering }: { postId: string; answering: boolean }): ReactElement {
  const path = `/groups/p/${encodeURIComponent(postId)}`;
  const [page, setPage] = useState<PostData | null>(null);
  const [failed, setFailed] = useState<'' | 'not_open' | 'gone' | 'load_failed'>('');
  const [composing, setComposing] = useState(answering);
  const [text, setText] = useState('');
  const [reply, setReply] = useState<{ to: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState<{ out: GroupCheck; to: Target; words: string } | null>(null);
  const [refusal, setRefusal] = useState<{ to: Target; r: Refusal } | null>(null);
  const [report, setReport] = useState<{ answerId: string | null } | null>(null);
  const dictation = useDictation();
  const box = useRef<HTMLTextAreaElement | null>(null);
  const replyBox = useRef<HTMLTextAreaElement | null>(null);
  const keptKey = `ember.group-answer.${postId}`;
  useKeptAcrossReload(keptKey, text.trim() ? text : '', (kept) => { setText(kept); setComposing(true); });

  const toAuth = (): void => navigate(`/auth?returnTo=${encodeURIComponent(`${path}${answering ? '?answer=1' : ''}`)}`, { replace: true });

  const reads = useRef(0);
  const load = (): void => {
    const seq = ++reads.current;
    emberApi.groupPost(postId)
      .then((r) => { if (seq === reads.current) { setPage(r); setFailed(''); } })
      .catch((err: unknown) => {
        if (seq !== reads.current) return;
        if (isAuthRequired(err)) { toAuth(); return; }
        const code = apiErrorCode(err);
        // Not in its group yet: the group's page, which offers Join when the group is open to them.
        const groupOf = err instanceof ApiError ? (err.body as { group_id?: string } | null)?.group_id : '';
        if (code === 'not_open' && groupOf) { navigate(`/groups/${encodeURIComponent(groupOf)}`, { replace: true }); return; }
        if (code === 'not_open') { setFailed('not_open'); return; }
        if (code === 'post_not_found') { setFailed('gone'); return; }
        trackFailed('group_post_page', apiFailureReason(err));
        setFailed('load_failed');
      });
  };
  useEffect(load, [postId]);
  useGroupsLive(load);

  // A box opened (by ?answer=1, Answer, Reply, or words waiting from before): in view, ready to type.
  useEffect(() => {
    if (!composing || !page || check) return;
    const el = box.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest' });
  }, [composing, Boolean(page), Boolean(check)]);
  useEffect(() => {
    if (!reply || check) return;
    const el = replyBox.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest' });
  }, [reply?.to, Boolean(check)]);

  if (failed === 'not_open') return <PageFailure title="This post is in a group that isn’t open to you" detail="Groups open up based on what you’ve talked about with Ember." logState="not_open" />;
  if (failed === 'gone') return <PageFailure title="This post isn’t here anymore" logState="not_found" />;
  if (!page && failed) return <PageFailure title="Couldn’t open this post" logState="load_failed" retry />;
  if (!page) return <PageLoading />;

  const { group, post, emoji } = page;
  const groupPath = `/groups/${encodeURIComponent(group.id)}`;
  const mine = post.author.me;
  const answerWord = post.kind === 'question' && !mine ? 'answer' : 'reply';
  const refusedAt = (to: Target): Refusal | null => (refusal && refusal.to === to ? refusal.r : null);

  /** Words back in the box they came from (the suggestion to change, or the words that did not go up). */
  const putBack = (to: Target, words: string): void => {
    if (to) setReply({ to, text: words });
    else { setText(words); setComposing(true); }
  };

  const refused = (err: unknown, to: Target): void => {
    if (isAuthRequired(err)) { toAuth(); return; }
    const code = apiErrorCode(err);
    if (code === 'not_open') { setFailed('not_open'); return; }
    if (code === 'post_not_found') { setFailed('gone'); return; }
    setCheck(null);
    setRefusal({ to, r: refusalOf(err, { text: to ? 'Your reply' : `Your ${answerWord}` }) });
  };

  const send = async (to: Target, words: string): Promise<void> => {
    await emberApi.answerGroupPost(postId, words, to);
    if (to) setReply(null);
    else {
      try { window.sessionStorage.removeItem(keptKey); } catch { /* nothing kept */ }
      setText(''); setComposing(false);
    }
    setCheck(null); setRefusal(null);
    load();
  };

  /** Ember's look, then up: as it is, or "Ember suggests …" first. */
  const next = async (to: Target, words: string): Promise<void> => {
    if (busy || !words) return;
    dictation.stop();
    setBusy(true); setRefusal(null);
    try {
      const out = await emberApi.checkGroupWords(group.id, { kind: 'answer', text: words });
      if (out.changes.length && out.suggested) { setCheck({ out, to, words }); return; }
      await send(to, words);
    } catch (err) {
      trackFailed('group_check', apiFailureReason(err), { match_id: group.id, state: to ? 'reply' : 'answer' });
      refused(err, to);
    } finally {
      setBusy(false);
    }
  };

  const take = async (): Promise<void> => {
    if (busy || !check) return;
    const { to } = check;
    const words = check.out.suggested?.text ?? check.words;
    setBusy(true);
    try {
      await send(to, words);
    } catch (err) {
      trackFailed(to ? 'group_reply' : 'group_answer', apiFailureReason(err), { match_id: group.id });
      putBack(to, words);
      refused(err, to);
    } finally {
      setBusy(false);
    }
  };

  // Emoji on the post (null) or an answer or reply (its id): drawn at once, the server's answer for the item then stands.
  const setReactions = (itemId: string | null, list: GroupReaction[]): void => {
    const on = <T extends GroupReply>(x: T): T => (x.id === itemId ? { ...x, reactions: list } : x);
    setPage((cur) => (!cur ? cur : {
      ...cur,
      post: itemId === null
        ? { ...cur.post, reactions: list }
        : { ...cur.post, answers: cur.post.answers.map((a) => ({ ...on(a), replies: a.replies.map(on) })) },
    }));
  };
  const react = async (itemId: string | null, current: GroupReaction[], chosen: string | null): Promise<void> => {
    setReactions(itemId, withMine(current, chosen));
    try {
      const out = await emberApi.reactInGroup(postId, chosen, itemId);
      setReactions(itemId, out.reactions);
    } catch (err) {
      trackFailed('group_react', apiFailureReason(err), { match_id: group.id, state: itemId ? 'answer' : 'post' });
      setReactions(itemId, current);
      toast.error('Couldn’t add that. Try again?');
    }
  };

  const sendReport = async (answerId: string | null): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await emberApi.reportGroupPost(postId, answerId);
      setReport(null);
      toast.success('Thanks for telling us. You won’t see it again.');
      if (answerId) {
        setPage((cur) => (cur ? {
          ...cur,
          post: { ...cur.post, answers: cur.post.answers.filter((a) => a.id !== answerId).map((a) => ({ ...a, replies: a.replies.filter((r) => r.id !== answerId) })) },
        } : cur));
      } else goBack(null, groupPath);
    } catch (err) {
      trackFailed('group_report', apiFailureReason(err), { match_id: group.id });
      toast.error('Couldn’t send that just now. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (check) {
    return (
      <CheckView
        kind="answer"
        reply={Boolean(check.to)}
        check={check.out}
        busy={busy}
        notice=""
        onTake={() => { void take(); }}
        onEdit={() => { putBack(check.to, check.out.suggested?.text ?? check.words); setCheck(null); }}
      />
    );
  }

  const openReply = (to: string): void => {
    if (reply?.to !== to) setReply({ to, text: '' });
    if (refusal?.to) setRefusal(null);
  };

  /** One answer or reply: who and when, the words, its emoji and Reply (which answers under the top-level answer `under`). */
  const said = (item: GroupReply, under: string, kind: 'answer' | 'reply'): ReactElement => (
    <>
      <div className="gr-answer-head">
        <GroupFaceView face={item.author} size={kind === 'reply' ? 24 : 28} />
        <span className="gr-answer-name" dir="auto">{item.author.me ? 'You' : item.author.name}</span>
        <span className="gr-answer-when">{ago(item.at)}</span>
        {!item.author.me && (
          <button type="button" className="gr-answer-more" aria-label={`Report this ${kind}`} data-log="group_report" data-log-state={`ask_${kind}`} data-log-match-id={group.id} onClick={() => setReport({ answerId: item.id })}>
            {MORE}
          </button>
        )}
      </div>
      <p className="gr-answer-text" dir="auto">{item.text}</p>
      <div className="gr-answer-foot">
        <GroupReactions reactions={item.reactions} emoji={emoji} groupId={group.id} what={kind} onReact={(e) => { void react(item.id, item.reactions, e); }} />
        <button type="button" className="gr-reply-open" data-log="group_reply_open" data-log-state={kind} data-log-match-id={group.id} onClick={() => openReply(under)}>
          Reply
        </button>
      </div>
    </>
  );

  const when = ago(post.at);
  const verb = post.kind === 'question' ? (mine ? 'You asked' : 'asks') : (mine ? 'You shared' : 'shares');
  const replyRefusal = reply ? refusedAt(reply.to) : null;
  const topRefusal = refusedAt(null);
  return (
    <main className="nf-page gr-page" data-log-seen="group_post_page" data-log-state={post.kind} data-log-match-id={group.id}>
      <GroupBar
        to={groupPath}
        title={group.name}
        right={mine ? null : (
          <button type="button" className="nf-iconbtn" aria-label="Report this post" data-log="group_report" data-log-state="ask" data-log-match-id={group.id} onClick={() => setReport({ answerId: null })}>
            {MORE}
          </button>
        )}
      />
      <section className="gr-op">
        <span className="gr-post-who">
          <GroupFaceView face={post.author} size={26} />
          <span className="gr-post-by">{mine ? verb : <><strong dir="auto">{post.author.name}</strong> {verb}</>}{when ? ` · ${when}` : ''}</span>
        </span>
        {post.kind === 'question'
          ? <>
            <h1 className="gr-op-question" dir="auto">{post.question}</h1>
            <p className="gr-op-body" dir="auto">{post.body}</p>
          </>
          : <h1 className="gr-op-share" dir="auto">{post.body}</h1>}
        {post.seen > 0 && <span className="gr-op-meta">Seen by {plural(post.seen, 'person', 'people')}</span>}
        <GroupReactions reactions={post.reactions} emoji={emoji} groupId={group.id} what="post" onReact={(e) => { void react(null, post.reactions, e); }} />
      </section>

      <div className="gr-answers">
        {post.answers.length === 0 && !composing && (
          <p className="gr-empty">{mine ? 'No answers yet. Everyone in the group can see it now, and you’ll hear when someone answers.' : post.kind === 'question' ? 'No answers yet. Yours could be the first.' : 'No replies yet.'}</p>
        )}
        {post.answers.map((a) => (
          <article className="gr-answer" key={a.id}>
            {said(a, a.id, 'answer')}
            {(a.replies.length > 0 || reply?.to === a.id) && (
              <div className="gr-replies">
                {a.replies.map((r) => <div className="gr-reply-item" key={r.id}>{said(r, a.id, 'reply')}</div>)}
                {reply?.to === a.id && (
                  <div className="gr-reply-box">
                    <GroupField
                      id={`group-reply-${a.id}`}
                      label="Your reply"
                      value={reply.text}
                      onChange={(v) => { setReply({ to: a.id, text: v }); if (replyRefusal && 'field' in replyRefusal) setRefusal(null); }}
                      dictation={dictation}
                      rows={2}
                      maxLength={GROUP_LIMITS.ANSWER}
                      placeholder="Write a reply…"
                      refused={replyRefusal && 'field' in replyRefusal ? replyRefusal.message : ''}
                      fieldRef={replyBox}
                    />
                    {replyRefusal && 'held' in replyRefusal && <HeldNote resources={replyRefusal.held} what="answer" />}
                    {replyRefusal && 'note' in replyRefusal && <p className="gr-refused" role="alert">{replyRefusal.note}</p>}
                    <div className="gr-reply-actions">
                      <button type="button" className="nf-text-btn" disabled={busy} data-log="group_reply" data-log-state="cancel" data-log-match-id={group.id}
                        onClick={() => { dictation.stop(); setReply(null); setRefusal(null); }}>
                        Not now
                      </button>
                      <button type="button" className="nf-cta" disabled={busy || !reply.text.trim()} data-log="group_reply" data-log-state="next" data-log-match-id={group.id}
                        onClick={() => { void next(a.id, reply.text.trim()); }}>
                        {busy ? 'Taking a look…' : 'Next'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </article>
        ))}

        {composing && (
          <section className="gr-reply">
            <GroupField
              id="group-answer"
              label={`Your ${answerWord}`}
              value={text}
              onChange={(v) => { setText(v); if (topRefusal && 'field' in topRefusal) setRefusal(null); }}
              dictation={dictation}
              rows={4}
              maxLength={GROUP_LIMITS.ANSWER}
              placeholder="Start typing…"
              refused={topRefusal && 'field' in topRefusal ? topRefusal.message : ''}
              fieldRef={box}
            />
            <span className="gr-field-hint">You’ll show up as <strong dir="auto">{group.me.name}</strong>.</span>
            {topRefusal && 'held' in topRefusal && <HeldNote resources={topRefusal.held} what="answer" />}
          </section>
        )}
      </div>

      {composing ? (
        <div className="nf-foot">
          {topRefusal && 'note' in topRefusal && <p className="nf-foot-note is-missing" role="alert">{topRefusal.note}</p>}
          <p className="nf-foot-note">Ember takes a quick look before it goes up.</p>
          <div className="gr-foot-row">
            <button type="button" className="nf-text-btn" disabled={busy} data-log="group_answer" data-log-state="cancel" data-log-match-id={group.id}
              onClick={() => { dictation.stop(); setComposing(false); if (refusal?.to === null) setRefusal(null); }}>
              Not now
            </button>
            <button type="button" className="nf-cta" disabled={busy || !text.trim()} data-log="group_check" data-log-state="answer" data-log-match-id={group.id} onClick={() => { void next(null, text.trim()); }}>
              {busy ? 'Taking a look…' : 'Next'}
            </button>
          </div>
        </div>
      ) : (
        <div className="nf-foot gr-foot-row is-bar">
          <button type="button" className="nf-cta" data-log="group_answer" data-log-state="open" data-log-match-id={group.id} onClick={() => setComposing(true)}>
            {answerWord === 'answer' ? 'Answer' : 'Reply'}
          </button>
        </div>
      )}

      {report && (
        <BottomSheet
          title={report.answerId ? 'Report this?' : 'Report this post?'}
          description="You won’t see it again, and the Ember team will take a look."
          logSeen="group_report_sheet"
          logSeenAttrs={{ 'data-log-match-id': group.id }}
          onClose={() => setReport(null)}
        >
          <button type="button" className="ember-btn nf-btn-dark" disabled={busy} data-log="group_report" data-log-state={report.answerId ? 'confirm_answer' : 'confirm'} data-log-match-id={group.id}
            onClick={() => { void sendReport(report.answerId); }}>
            Report
          </button>
          <button type="button" className="ember-btn ember-btn-ghost" data-log="group_report" data-log-state="keep" onClick={() => setReport(null)}>
            Cancel
          </button>
        </BottomSheet>
      )}
    </main>
  );
}
